/**
 * PerformanceAnalyticsContainer - Logic for player performance analytics
 * Manages state and data for the performance analytics visualization.
 * Uses onLayout measurement to derive responsive chart sizes so the spider
 * graph adapts to available width (e.g. when sidebar expands/collapses).
 * On mobile, computes responsive chart dimensions and passes individual
 * DotPlot props to the View for direct rendering (avoids Victory SVG
 * painting issues with pre-memoized ReactNodes).
 * Accepts a `renderAdditionalContent` render prop instead of a plain ReactNode
 * so downstream components (e.g. IndividualMetricInsights) can receive the
 * comparison player and render an orange trend line alongside existing data.
 * @remarks This is a Container component - handles business logic and state.
 * Metric labels and scores dynamically include organization custom metrics
 * from bioData.customStats when available.
 * League ID for comparison filtering uses a chained fallback
 * (player.team.league.id → player.league.id) to prevent silent failures
 * where null league IDs cause the distribution chart to pull players from
 * all leagues globally (SE-3833).
 * Distribution dots are extracted client-side from `useComparisonData`
 * players (SE-3984); the card captions a pending, failed or empty comparison
 * rather than drawing the player dot alone (SE-8283).
 * `isSubmenuLoading` is derived per-category (watchlists/leagues
 * each use their own query's loading flag) so the submenu shows a spinner
 * whenever its specific data is in flight.
 */

import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useWindowDimensions } from "react-native";

import { useDebouncedCallback } from "use-debounce";

import PerformanceAnalyticsView from "./PerformanceAnalyticsView";
import { POSITIONS_FULL_LIST_FIRST } from "@/config/constants";
import { MY_WATCHLISTS_PAGE_SIZE } from "@/features/watchlist/constants";
import { useChartCaptureRefs } from "@/features/player-detail/hooks/useChartCaptureRefs";
import { useComparePlayerSearch } from "@/features/player-detail/hooks/useComparePlayerSearch";
import useCompareSubmenu from "@/features/player-detail/hooks/useCompareSubmenu";
import { useComparisonData } from "@/features/player-detail/hooks/useComparisonData";
import { useDotPlotData } from "@/features/player-detail/hooks/useDotPlotData";
import { usePerformanceMenuTriggers } from "@/features/player-detail/hooks/usePerformanceMenuTriggers";
import { usePerformanceDropdowns } from "@/features/player-detail/hooks/usePerformanceDropdowns";
import { useResponsiveChartSizes } from "@/features/player-detail/hooks/useResponsiveChartSizes";
import RadarChartWithSelectors from "@/features/player-detail/components/organisms/RadarChartWithSelectors";
import {
  buildDistributionMetrics,
  buildRadarData,
  buildSeasonOptions,
  buildSeasonOptionsFromWeightedAvgMetrics,
  extractComparisonPlayerScores,
  extractPlayerScoresForSeason,
  resolveDistributionComparisonStatus,
  updateMetricsWithScores,
  type MetricOption,
  type SelectedMetrics,
} from "@/features/player-detail/utils/performanceAnalyticsUtils";

import {
  SPIDER_CHART_SIZE,
  COMPARE_CATEGORY_OPTIONS,
  getMetricLabelsWithCustom,
  getDefaultLabelsForPosition,
  getMetricDescriptionsForPosition,
} from "@/features/player-detail/constants";
import type {
  CompareItem,
  TimeframeOption,
} from "@/features/player-detail/types";
import type { BackendCustomStats } from "@/types/customMetrics";
import {
  PlayerDetailPlayerMetaFragment,
  PerformanceAnalyticsPlayerScoresFragment,
  useWatchlistListMyUserWatchlistsQuery,
  useRosterManagementListPositionsQuery,
  useRosterManagementListMyOrganizationsLeaguesQuery,
} from "@/generated/graphql";
import {
  calculateNiceMaxValue,
  getAxisStyles,
  getChartDimensions,
  getDashboardCardTheme,
  RadarDataPoint,
} from "@/utils/radarChartUtils";

/** Total horizontal padding from page edges to carousel content (px-3 header + p-3 DataHub) */
const MOBILE_PAGE_HORIZONTAL_PADDING = 48;

/** Internal padding per card side (p-3 = 12px × 2) */
const MOBILE_CARD_CONTENT_PADDING = 24;

/** Spider chart padding per screen size: selector = outer label padding, chart = Victory internal padding */
const SPIDER_PADDING = {
  mobile: { selector: 50, chart: 35 },
  desktop: { selector: 50, chart: 25 },
} as const;

const INITIAL_X_METRIC: MetricOption = { label: "Passing", value: null };
const INITIAL_Y_METRIC: MetricOption = { label: "Dribbling", value: null };

/**
 * Props for PerformanceAnalyticsContainer component
 */
interface PerformanceAnalyticsContainerProps {
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Player data fragment */
  readonly player?: PlayerDetailPlayerMetaFragment | null;
  /** Whether the screen is mobile sized */
  readonly isSmallScreen: boolean;
  /** Render prop for the bottom-right grid cell; receives the comparison player (or null) so downstream components can render an orange comparison line */
  readonly renderAdditionalContent?: (
    comparisonPlayer: PerformanceAnalyticsPlayerScoresFragment | null
  ) => React.ReactNode;
}

/**
 * PerformanceAnalyticsContainer manages state and data for performance analytics
 * @param props - Container props
 * @param props.isDark - Whether dark mode is active
 * @param props.player - Player data fragment
 * @param props.isSmallScreen - Whether the screen is mobile sized
 * @param props.renderAdditionalContent - Render prop for the bottom-right grid cell; receives the comparison player
 * @returns PerformanceAnalyticsView with computed props
 */
const PerformanceAnalyticsContainer = ({
  isDark,
  player,
  isSmallScreen,
  renderAdditionalContent,
}: PerformanceAnalyticsContainerProps) => {
  const { width: screenWidth } = useWindowDimensions();

  // Card width for mobile dot-plot sizing
  const cardWidth = isSmallScreen
    ? screenWidth - MOBILE_PAGE_HORIZONTAL_PADDING
    : 0;

  // Chart capture refs for PDF generation (web only)
  const { spiderChartRef, dotPlotRef } = useChartCaptureRefs();

  // Responsive chart sizing - adapts to panel width changes (sidebar toggle)
  const {
    handleLeftPanelLayout,
    responsiveChartSize,
    responsiveDotPlotSize,
    chartDimensions,
  } = useResponsiveChartSizes(SPIDER_PADDING.desktop.selector);

  // Determine if this player is a goalkeeper based on position data
  const isGoalkeeper = useMemo(
    () =>
      player?.positions?.some(pos =>
        pos?.name?.toLowerCase().includes("goalkeeper")
      ) ?? false,
    [player?.positions]
  );

  const [xAxisMetric, setXAxisMetric] = useState(INITIAL_X_METRIC);
  const [yAxisMetric, setYAxisMetric] = useState(INITIAL_Y_METRIC);

  // Compare VS dropdown state - category and selected item (defaults to player's position)
  const [selectedCompareCategory, setSelectedCompareCategory] =
    useState<CompareItem | null>(null);
  const [selectedCompareItem, setSelectedCompareItem] =
    useState<CompareItem | null>(null);
  const [selectedCompareCategoryType, setSelectedCompareCategoryType] =
    useState<string>("position");
  const [compareSubmenuSearch, setCompareSubmenuSearch] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  // Latches true once user opens Compare VS dropdown — queries stay active thereafter
  const [hasOpenedCompareMenu, setHasOpenedCompareMenu] = useState(false);
  const { data: watchlistsData, loading: watchlistsLoading } =
    useWatchlistListMyUserWatchlistsQuery({
      variables: { first: MY_WATCHLISTS_PAGE_SIZE },
      skip: !hasOpenedCompareMenu,
    });
  // `$first` must cover the FULL taxonomy: when omitted the backend returns
  // an arbitrary 20-row default page — on staging Goalkeeper is row 21 and
  // was silently missing from the compare submenu (SE-5481).
  const { data: positionsData } = useRosterManagementListPositionsQuery({
    variables: { first: POSITIONS_FULL_LIST_FIRST },
  });
  const { data: leaguesData, loading: leaguesLoading } =
    useRosterManagementListMyOrganizationsLeaguesQuery({
      variables: { first: 200 },
      skip: !hasOpenedCompareMenu,
    });

  const debouncedSetSearchQuery = useDebouncedCallback(
    setDebouncedSearchQuery,
    300
  );

  // SE-7111: the compare-vs-player search is a PAGINATED connection, not a
  // one-shot top-ten. See the hook for why it does not reuse
  // `usePlayerSearchPagination`.
  const {
    playerSearchResults,
    playersLoading,
    hasMorePlayers,
    isLoadingMorePlayers,
    loadMorePlayers,
  } = useComparePlayerSearch(debouncedSearchQuery);

  // Build season options; fall back to weighted avg metrics for GKs with empty seasonCategoricalScores
  const availableSeasons = useMemo(() => {
    const options = buildSeasonOptions(player);
    return options.length > 0
      ? options
      : buildSeasonOptionsFromWeightedAvgMetrics(player);
  }, [player]);

  const [selectedTimeframe, setSelectedTimeframe] =
    useState<TimeframeOption | null>(null);

  // Main player's primary position for comparison baseline
  const mainPlayerPosition = useMemo((): CompareItem | null => {
    const pos = player?.positions?.[0];
    return pos ? { id: pos.id, name: pos.name } : null;
  }, [player]);

  // Main player's league ID — chained fallback: team.league.id → player.league.id
  // Both paths return the same value per backend confirmation (SE-3833).
  const mainPlayerLeagueId =
    player?.team?.league?.id ?? player?.league?.id ?? null;

  if (__DEV__ && player && !mainPlayerLeagueId)
    console.warn(
      `[PerformanceAnalytics] League ID resolved to null for player "${player.id}". Distribution chart will show global results.`
    );

  // Fetch comparison data based on selected category type and item
  const {
    averages: fetchedAverages,
    players: comparisonPlayers,
    status: comparisonStatus,
  } = useComparisonData(
    selectedCompareCategoryType,
    selectedCompareItem,
    player?.id ?? "",
    selectedTimeframe?.id,
    mainPlayerLeagueId
  );

  // Always fetch position data for baseline context (gray line/dots)
  const {
    averages: positionAverages,
    players: positionPlayers,
    status: positionStatus,
  } = useComparisonData(
    "position",
    mainPlayerPosition,
    player?.id ?? "",
    selectedTimeframe?.id,
    mainPlayerLeagueId
  );

  const isGroupComparison = ["position", "leagues", "watch_list"].includes(
    selectedCompareCategoryType
  );

  // Check if an actual player is selected (not the category placeholder)
  const hasActualPlayerSelected =
    selectedCompareCategoryType === "players" &&
    selectedCompareItem?.id !== "players";

  // Season-specific scores (falls back to global if no season data)
  const playerScores = useMemo(
    () =>
      extractPlayerScoresForSeason(player, selectedTimeframe?.id, isGoalkeeper),
    [player, selectedTimeframe?.id, isGoalkeeper]
  );

  // Use fetched averages from backend when available
  const comparisonAverages: Record<string, number | null> = useMemo(
    () => fetchedAverages ?? {},
    [fetchedAverages]
  );

  // Position-aware default labels for initial metric selection
  const defaultLabels = useMemo(
    () => getDefaultLabelsForPosition(isGoalkeeper),
    [isGoalkeeper]
  );

  // All metric labels including custom metrics (used for axis label validation)
  const metricLabels = useMemo(
    () =>
      getMetricLabelsWithCustom(
        isGoalkeeper,
        player?.bioData?.customStats as BackendCustomStats | null | undefined
      ),
    [isGoalkeeper, player?.bioData?.customStats]
  );

  // Initialize selected metrics with default labels
  const [selectedMetrics, setSelectedMetrics] = useState<SelectedMetrics>(() =>
    defaultLabels.reduce(
      (acc, label, index) => ({
        ...acc,
        [index]: { label, value: playerScores[label] ?? null },
      }),
      {} as Record<number, MetricOption>
    )
  );

  // Reset selected metrics when player position type changes (GK vs outfield)
  // or when current axis labels don't belong to the current position's metric set
  // (catches initial GK load where axes default to outfield labels).
  // Validates against all metric labels (including custom) so custom metric
  // selections are not incorrectly reset.
  const [prevIsGoalkeeper, setPrevIsGoalkeeper] = useState(isGoalkeeper);
  const xLabelValid = metricLabels.includes(xAxisMetric.label);
  const yLabelValid = metricLabels.includes(yAxisMetric.label);
  const needsMetricReset =
    prevIsGoalkeeper !== isGoalkeeper || !xLabelValid || !yLabelValid;

  if (needsMetricReset) {
    if (prevIsGoalkeeper !== isGoalkeeper) {
      setPrevIsGoalkeeper(isGoalkeeper);
    }
    setSelectedMetrics(
      defaultLabels.reduce(
        (acc, label, index) => ({
          ...acc,
          [index]: { label, value: playerScores[label] ?? null },
        }),
        {} as Record<number, MetricOption>
      )
    );
    const xLabel = defaultLabels[0] ?? "Passing";
    const yLabel = defaultLabels[1] ?? "Dribbling";
    setXAxisMetric({ label: xLabel, value: playerScores[xLabel] ?? null });
    setYAxisMetric({ label: yLabel, value: playerScores[yLabel] ?? null });
  }

  // Info modal handlers
  const handleOpenInfoModal = useCallback(() => setIsInfoModalOpen(true), []);
  const handleCloseInfoModal = useCallback(() => setIsInfoModalOpen(false), []);

  // Compare submenu handlers
  const clearSubmenuState = useCallback(() => {
    setSelectedCompareCategory(null);
    setCompareSubmenuSearch("");
    setDebouncedSearchQuery("");
  }, []);

  const handleSelectSubmenuItem = useCallback(
    (item: CompareItem) => {
      setSelectedCompareItem(item);
      if (selectedCompareCategory)
        setSelectedCompareCategoryType(selectedCompareCategory.id);
      clearSubmenuState();
    },
    [selectedCompareCategory, clearSubmenuState]
  );

  const handleSubmenuSearchChange = useCallback(
    (q: string) => {
      setCompareSubmenuSearch(q);
      debouncedSetSearchQuery(q);
    },
    [debouncedSetSearchQuery]
  );
  // SE-5551: open-state + trigger-anchored positioning for the compare /
  // timeframe dropdowns, now hosted by FixedPositionDropdownShell (fixes the
  // Android mispositioning of the old gluestack Menu).
  const dropdowns = usePerformanceDropdowns();

  const createCompareCategorySelectHandler = useCallback(
    (item: CompareItem) => () => {
      dropdowns.closeCompareDropdown();
      setHasOpenedCompareMenu(true);
      setSelectedCompareCategory(item);
    },
    [dropdowns]
  );
  const createTimeframeSelectHandler = useCallback(
    (tf: TimeframeOption) => () => {
      dropdowns.closeTimeframeDropdown();
      setSelectedTimeframe(tf);
    },
    [dropdowns]
  );
  // SE-5568: shell-hosted axis dropdowns close on select, like compare above.
  // The SE-3823 setTimeout deferral is gone with the RN Modal it worked around.
  const createYSelectHandler = useCallback(
    (m: MetricOption) => () => {
      dropdowns.closeYAxisDropdown();
      setYAxisMetric(m);
    },
    [dropdowns]
  );
  const createXSelectHandler = useCallback(
    (m: MetricOption) => () => {
      dropdowns.closeXAxisDropdown();
      setXAxisMetric(m);
    },
    [dropdowns]
  );

  // Menu trigger render functions from hook
  const {
    renderCompareMenuTrigger,
    renderTimeframeMenuTrigger,
    renderXMenuTrigger,
    renderYMenuTrigger,
  } = usePerformanceMenuTriggers({
    isDark,
    selectedCompareItemName: selectedCompareItem?.name ?? "Position",
    selectedTimeframeLabel: selectedTimeframe?.label ?? "Career",
    xAxisMetric,
    yAxisMetric,
  });

  /** Shared handler for metric selection (radar chart selectors and distribution rows) */
  const handleMetricChange = useCallback(
    (index: number, metric: MetricOption) =>
      setSelectedMetrics(prev => {
        // Swap with existing position when a duplicate label would be created
        const existingIndex = Object.keys(prev)
          .map(Number)
          .find(i => i !== index && prev[i]?.label === metric.label);
        return existingIndex !== undefined
          ? { ...prev, [index]: metric, [existingIndex]: prev[index] }
          : { ...prev, [index]: metric };
      }),
    []
  );

  const playerName = useMemo(() => player?.name ?? "Unknown Player", [player]);

  const availableMetrics: readonly MetricOption[] = useMemo(
    () =>
      metricLabels.map((label: string) => ({
        label,
        value: playerScores[label] ?? null,
      })),
    [metricLabels, playerScores]
  );

  // Update selected metrics values when player scores change
  const selectedMetricsWithValues = useMemo(
    () => updateMetricsWithScores(selectedMetrics, playerScores),
    [selectedMetrics, playerScores]
  );

  const theme = useMemo(() => {
    const cardTheme = getDashboardCardTheme(isDark);
    return {
      containerClass: cardTheme.containerClass,
      headingClass: cardTheme.headingClass,
      textClass: "text-content-secondary",
    };
  }, [isDark]);

  // Build radar data from selected metrics
  const primaryRadarData: readonly RadarDataPoint[] = useMemo(
    () => buildRadarData(selectedMetricsWithValues, metric => metric.value),
    [selectedMetricsWithValues]
  );

  const isPositionComparison = selectedCompareCategoryType === "position";

  // Secondary radar data (gray dashed) - position avg for player comparison, else comparison group avg
  const secondaryRadarData: readonly RadarDataPoint[] = useMemo(
    () =>
      buildRadarData(selectedMetricsWithValues, metric =>
        hasActualPlayerSelected
          ? (positionAverages?.[metric.label] ?? null)
          : (comparisonAverages[metric.label] ?? null)
      ),
    [
      selectedMetricsWithValues,
      hasActualPlayerSelected,
      positionAverages,
      comparisonAverages,
    ]
  );

  // Tertiary radar data (orange line) - only for individual player comparison
  const tertiaryRadarData: readonly RadarDataPoint[] = useMemo(
    () =>
      hasActualPlayerSelected
        ? buildRadarData(
            selectedMetricsWithValues,
            metric => comparisonAverages[metric.label] ?? null
          )
        : [],
    [selectedMetricsWithValues, hasActualPlayerSelected, comparisonAverages]
  );

  // Comparison player list - group players for group comparison, position players for individual.
  // Used by both distribution charts and scatter plot background dots.
  const seasonKey = selectedTimeframe?.id;
  const comparisonPlayerList = useMemo(() => {
    const playersToUse = isGroupComparison
      ? comparisonPlayers
      : positionPlayers;
    return (
      playersToUse?.map(p => ({
        id: p.id,
        name: p?.name ?? "",
        scores: extractComparisonPlayerScores(p, seasonKey),
      })) ?? []
    );
  }, [isGroupComparison, comparisonPlayers, positionPlayers, seasonKey]);

  const metrics = useMemo(
    () =>
      buildDistributionMetrics(selectedMetricsWithValues, comparisonPlayerList),
    [selectedMetricsWithValues, comparisonPlayerList]
  );

  // SE-8283: the query feeding comparisonPlayerList decides the caption. A
  // string from one pass over memoized rows, so it needs no memo of its own.
  const distributionComparisonStatus = resolveDistributionComparisonStatus(
    isGroupComparison ? comparisonStatus : positionStatus,
    metrics
  );

  const axisStyles = useMemo(() => getAxisStyles(isDark), [isDark]);
  const independentAxisStyle = useMemo(
    () => ({
      ...axisStyles.independentAxisStyle,
      tickLabels: { fill: "transparent" },
    }),
    [axisStyles.independentAxisStyle]
  );

  // Compute dynamic max for radar chart domain to handle scores > 100
  const radarMaxValue = useMemo(() => {
    const yValues = [primaryRadarData, secondaryRadarData, tertiaryRadarData]
      .flatMap(data => data.map(d => d.y))
      .filter((v): v is number => v != null);
    return calculateNiceMaxValue(yValues.length > 0 ? Math.max(...yValues) : 0);
  }, [primaryRadarData, secondaryRadarData, tertiaryRadarData]);

  // Mobile spider chart uses smaller chart + reduced selector padding to fit card width
  const spiderChartSize = isSmallScreen
    ? cardWidth -
      MOBILE_CARD_CONTENT_PADDING -
      SPIDER_PADDING.mobile.selector * 2
    : SPIDER_CHART_SIZE;

  const spiderChartDimensions = useMemo(
    () =>
      getChartDimensions(
        spiderChartSize,
        spiderChartSize,
        spiderChartSize,
        spiderChartSize
      ),
    [spiderChartSize]
  );

  const radarChartContent = useMemo(
    () => (
      <RadarChartWithSelectors
        isDark={isDark}
        primaryData={primaryRadarData}
        secondaryData={secondaryRadarData}
        tertiaryData={tertiaryRadarData}
        selectedMetrics={selectedMetricsWithValues}
        availableMetrics={availableMetrics}
        onMetricChange={handleMetricChange}
        chartSize={isSmallScreen ? spiderChartSize : responsiveChartSize}
        chartDimensions={
          isSmallScreen ? spiderChartDimensions : chartDimensions
        }
        dependentAxisStyle={axisStyles.dependentAxisStyle}
        independentAxisStyle={independentAxisStyle}
        scatterStrokeColor={axisStyles.scatterStrokeColor}
        maxValue={radarMaxValue}
        selectorPadding={
          isSmallScreen
            ? SPIDER_PADDING.mobile.selector
            : SPIDER_PADDING.desktop.selector
        }
        chartPadding={
          isSmallScreen
            ? SPIDER_PADDING.mobile.chart
            : SPIDER_PADDING.desktop.chart
        }
      />
    ),
    [
      isDark,
      primaryRadarData,
      secondaryRadarData,
      tertiaryRadarData,
      selectedMetricsWithValues,
      availableMetrics,
      handleMetricChange,
      isSmallScreen,
      spiderChartSize,
      spiderChartDimensions,
      responsiveChartSize,
      chartDimensions,
      axisStyles.dependentAxisStyle,
      independentAxisStyle,
      axisStyles.scatterStrokeColor,
      radarMaxValue,
    ]
  );

  // Update axis metrics with current player scores
  const xMetricWithValue: MetricOption = useMemo(
    () => ({
      label: xAxisMetric.label,
      value: playerScores[xAxisMetric.label] ?? null,
    }),
    [xAxisMetric.label, playerScores]
  );
  const yMetricWithValue: MetricOption = useMemo(
    () => ({
      label: yAxisMetric.label,
      value: playerScores[yAxisMetric.label] ?? null,
    }),
    [yAxisMetric.label, playerScores]
  );

  // Scatter plot data, tooltip formatter, and pre-rendered DotPlot content
  const {
    mobileDotPlotSize,
    scatterPlotPlayers,
    dotPlotTooltipLabelFormatter,
    dotPlotContent,
  } = useDotPlotData({
    isGroupComparison,
    comparisonPlayers,
    positionPlayers,
    seasonKey,
    hasActualPlayerSelected,
    selectedCompareItem,
    comparisonAverages,
    playerName,
    xMetricWithValue,
    yMetricWithValue,
    playerScores,
    availableMetrics,
    isDark,
    isSmallScreen,
    cardWidth,
    desktopDotPlotSize: responsiveDotPlotSize,
    renderYMenuTrigger,
    renderXMenuTrigger,
    createYSelectHandler,
    createXSelectHandler,
    xAxisDropdown: dropdowns.xAxisDropdown,
    yAxisDropdown: dropdowns.yAxisDropdown,
  });

  // Submenu filtering, titles, placeholders, and loading state
  const {
    filteredSubmenuItems,
    submenuTitle,
    submenuSearchPlaceholder,
    isSubmenuLoading,
  } = useCompareSubmenu({
    watchlistsData,
    positionsData,
    leaguesData,
    selectedCompareCategory,
    compareSubmenuSearch,
    debouncedSearchQuery,
    playerSearchResults,
    playersLoading,
    watchlistsLoading,
    leaguesLoading,
  });

  /* Paging belongs to the player search alone — the other categories are
   * whole lists already in hand, filtered client-side, with no next page to
   * fetch. Gating here keeps the affordance off those submenus entirely.
   *
   * The row-count guard is not belt-and-braces. Re-typing a term the cache has
   * already answered puts Apollo in `setVariables` with the cached connection
   * already in `data`: `hasNextPage` reads true off it, while `playersLoading`
   * is still true so `useCompareSubmenu` renders no rows. Without this, "Load
   * more" would paint UNDER a "Searching..." placeholder and do nothing when
   * pressed, because `useInfiniteScroll` treats `setVariables` as busy. The
   * invariant is stated here, where the list and the flag are both in scope:
   * the control never renders without a list to extend. */
  const isPlayerCompareSubmenu = selectedCompareCategory?.id === "players";
  const hasMoreSubmenuItems =
    isPlayerCompareSubmenu && hasMorePlayers && filteredSubmenuItems.length > 0;
  const isLoadingMoreSubmenuItems =
    isPlayerCompareSubmenu && isLoadingMorePlayers;

  // Set default timeframe and comparison when data first loads
  useEffect(() => {
    /* eslint-disable react-hooks/set-state-in-effect -- Initial sync */
    if (availableSeasons.length > 0 && !selectedTimeframe)
      setSelectedTimeframe(availableSeasons[0]);
    const pos = player?.positions?.[0];
    if (pos && !selectedCompareItem)
      setSelectedCompareItem({ id: pos.id, name: pos.name });
    /* eslint-enable react-hooks/set-state-in-effect -- end initial sync */
  }, [
    availableSeasons,
    selectedTimeframe,
    player?.positions,
    selectedCompareItem,
  ]);

  if (!player) return null;

  return (
    <PerformanceAnalyticsView
      isDark={isDark}
      isSmallScreen={isSmallScreen}
      additionalContent={renderAdditionalContent?.(
        hasActualPlayerSelected ? (comparisonPlayers?.[0] ?? null) : null
      )}
      mobileDotPlotSize={mobileDotPlotSize}
      scatterPlotPlayers={scatterPlotPlayers}
      xMetricWithValue={xMetricWithValue}
      yMetricWithValue={yMetricWithValue}
      renderYMenuTrigger={renderYMenuTrigger}
      renderXMenuTrigger={renderXMenuTrigger}
      createYSelectHandler={createYSelectHandler}
      createXSelectHandler={createXSelectHandler}
      xAxisDropdown={dropdowns.xAxisDropdown}
      yAxisDropdown={dropdowns.yAxisDropdown}
      dotPlotTooltipLabelFormatter={dotPlotTooltipLabelFormatter}
      playerName={playerName}
      spiderChartRef={spiderChartRef}
      dotPlotRef={dotPlotRef}
      onLeftPanelLayout={handleLeftPanelLayout}
      selectedCompareItem={selectedCompareItem}
      selectedTimeframe={selectedTimeframe}
      radarChartContent={radarChartContent}
      dotPlotContent={dotPlotContent}
      metrics={metrics}
      distributionComparisonStatus={distributionComparisonStatus}
      theme={theme}
      availableMetrics={availableMetrics}
      onSelectDistributionMetricAtIndex={handleMetricChange}
      compareCategoryOptions={COMPARE_CATEGORY_OPTIONS}
      renderCompareMenuTrigger={renderCompareMenuTrigger}
      createCompareCategorySelectHandler={createCompareCategorySelectHandler}
      compareDropdown={dropdowns.compareDropdown}
      timeframeOptions={availableSeasons}
      renderTimeframeMenuTrigger={renderTimeframeMenuTrigger}
      createTimeframeSelectHandler={createTimeframeSelectHandler}
      timeframeDropdown={dropdowns.timeframeDropdown}
      isSubmenuOpen={selectedCompareCategory !== null}
      submenuTitle={submenuTitle}
      submenuSearchPlaceholder={submenuSearchPlaceholder}
      submenuSearchQuery={compareSubmenuSearch}
      filteredSubmenuItems={filteredSubmenuItems}
      isSubmenuLoading={isSubmenuLoading}
      hasMoreSubmenuItems={hasMoreSubmenuItems}
      isLoadingMoreSubmenuItems={isLoadingMoreSubmenuItems}
      onLoadMoreSubmenuItems={loadMorePlayers}
      showComparisonDistribution={isGroupComparison && !isPositionComparison}
      isPositionComparison={isPositionComparison}
      isPlayerComparison={hasActualPlayerSelected}
      selectedCompareCategoryType={selectedCompareCategoryType}
      comparisonAverages={comparisonAverages}
      onCloseSubmenu={clearSubmenuState}
      onSelectSubmenuItem={handleSelectSubmenuItem}
      onSubmenuSearchChange={handleSubmenuSearchChange}
      isInfoModalOpen={isInfoModalOpen}
      onOpenInfoModal={handleOpenInfoModal}
      onCloseInfoModal={handleCloseInfoModal}
      metricDescriptions={getMetricDescriptionsForPosition(isGoalkeeper)}
    />
  );
};

PerformanceAnalyticsContainer.displayName = "PerformanceAnalyticsContainer";

export default React.memo(PerformanceAnalyticsContainer);
